import {
  memo, useCallback, useEffect, SyntheticEvent,
} from 'react';
import { type TFunction } from 'i18next';
import { Control, UseFormSetValue } from 'react-hook-form';
import { IComponentWithCustomScrollbarProps } from '../../services';
import {
  ReactHookFormAutocomplete,
  TAutocompleteDataSourceResponseParser,
} from '../Autocomplete';
import { validateNoSpaces } from '../../validators';

export enum VaultApplicationTypeEnum {
  Website = 'Website',
  DesktopApp = 'DesktopApp'
}

export interface IServiceSelectorProps extends IComponentWithCustomScrollbarProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  websitesDatasourceRequest: (searchStr: string) => Promise<any[]>;
  desktopAppsDatasourceRequest: (searchStr?: string) => Promise<any[]>;
  websitesOptionsParser: TAutocompleteDataSourceResponseParser;
  desktopAppsOptionsParser: TAutocompleteDataSourceResponseParser;
  vaultApplicationType: VaultApplicationTypeEnum | string;
  desktopAppLabel: string;
  desktopAppPlaceholder: string;
  websiteLabel: string;
  websitePlaceholder: string;
  noSpacesValidationErrorMessage: string;
  t: TFunction<'translation'>;
  websiteFieldName?: string;
  applicationIdFieldName?: string;
  desktopAppFieldName?: string;
  applicationId?: string;
}

const ServiceSelector = (props: IServiceSelectorProps) => {
  const {
    vaultApplicationType,
    control,
    setValue,
    desktopAppsDatasourceRequest,
    websitesDatasourceRequest,
    websitesOptionsParser,
    desktopAppsOptionsParser,
    desktopAppPlaceholder,
    desktopAppLabel,
    websitePlaceholder,
    websiteLabel,
    noSpacesValidationErrorMessage,
    t,
    websiteFieldName = 'website',
    desktopAppFieldName = 'desktopApp',
    applicationIdFieldName = 'applicationId',
    disableCustomScrollbar = false,
    applicationId,
  } = props;

  useEffect(() => {
    if (!applicationId) return;
    setValue(applicationIdFieldName, applicationId);
  }, [applicationIdFieldName, setValue, applicationId]);

  const handleWebsiteOptionChange = useCallback((event: SyntheticEvent, value: any) => {
    if (!value?.id) return;
    setValue(websiteFieldName, value.label);
    setValue(applicationIdFieldName, value.id);
  }, [setValue, websiteFieldName, applicationIdFieldName]);

  const handleDesktopOptionChange = useCallback((event: SyntheticEvent, value: any) => {
    if (!value?.id) return;
    setValue(applicationIdFieldName, value.id);
  }, [setValue, applicationIdFieldName]);

  const handleWebsiteInputChange = useCallback((event: SyntheticEvent, value: string) => {
    setValue(websiteFieldName, value);
    setValue(applicationIdFieldName, null);
  }, [setValue, websiteFieldName, applicationIdFieldName]);

  const handleDesktopAppInputChange = useCallback((
    event: SyntheticEvent,
    value: string,
    reason: string,
  ) => {
    if (reason === 'clear') {
      setValue(applicationIdFieldName, null);
    }
  }, [setValue, applicationIdFieldName]);

  if (vaultApplicationType === VaultApplicationTypeEnum.Website) {
    return (
      <ReactHookFormAutocomplete
        key={websiteFieldName}
        fullWidth
        freeSolo
        t={t}
        name={websiteFieldName}
        strictOptionsCompare={false}
        autoSelect={false}
        filterSelectedOptions={false}
        onChange={handleWebsiteOptionChange}
        onInputChange={handleWebsiteInputChange}
        placeholder={websitePlaceholder}
        label={websiteLabel}
        dataSourceRequest={websitesDatasourceRequest}
        dataSourceResponseParser={websitesOptionsParser}
        control={control}
        rules={{
          validate: {
            noSpaces: (
              option,
            ) => validateNoSpaces(option?.label ?? option) || noSpacesValidationErrorMessage,
          },
        }}
        disableCustomScrollbar={disableCustomScrollbar}
      />
    );
  }

  return (
    <ReactHookFormAutocomplete
      key={desktopAppFieldName}
      fullWidth
      fetchOnEmptyQuery
      strictOptionsCompare={false}
      t={t}
      filterSelectedOptions={false}
      name={desktopAppFieldName}
      dataSourceRequest={desktopAppsDatasourceRequest}
      dataSourceResponseParser={desktopAppsOptionsParser}
      onChange={handleDesktopOptionChange}
      onInputChange={handleDesktopAppInputChange}
      label={desktopAppLabel}
      placeholder={desktopAppPlaceholder}
      control={control}
      disableCustomScrollbar={disableCustomScrollbar}
    />
  );
};

export default memo(ServiceSelector);
